import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { drivers } from "@/api/client";
import { useConfirm } from "@/components/ConfirmDialog";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { dateTime } from "@/lib/format";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { ChangeNumber, DriverChip, DriverInfoPanel, KycSection, PhotoSection } from "@/modules/drivers/common";

import { Icon } from "@/components/icons";
/** Edit Driver = every mutation in one place (single-subject page: picker +
 *  inline sections, per the module grammar). Overview's ✎ Edit deep-links
 *  here with ?id=. */
export function EditDriverPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const sel = params.get("id");
  const [pick, setPick] = useState("");

  const fb = useMutationFeedback();
  const { ask, dialog } = useConfirm();

  // list only once the user has typed — an unprompted dump of the whole
  // estate isn't a picker, it's noise
  const hasTerm = pick.trim().length > 0;
  const results = useQuery({
    queryKey: ["drivers", "list", "", pick],
    queryFn: () => drivers.list({ search: pick.trim(), limit: "10" }),
    enabled: !sel && hasTerm,
  });
  const detail = useQuery({
    queryKey: ["drivers", "detail", sel],
    queryFn: () => drivers.detail(sel!),
    enabled: !!sel,
  });
  const d = detail.data;

  const refreshDetail = () => {
    void qc.invalidateQueries({ queryKey: ["drivers"] });
  };
  // edit-in-place: the field is SEEDED with the current name once detail
  // loads — editing existing info, not entering a new record
  const [newName, setNewName] = useState("");
  useEffect(() => { if (d) setNewName(d.name); }, [d?.driver_id, d?.name]);
  const nameDirty = !!d && newName.trim() !== "" && newName.trim() !== d.name;
  const renameMut = useMutation({
    mutationFn: () => drivers.rename(d!.driver_id, newName.trim()),
    onSuccess: () => { fb.ok(); refreshDetail(); },
    onError: fb.fail,
  });
  const lifecycle = useMutation({
    mutationFn: (action: "block" | "unblock" | "remove") =>
      drivers.lifecycle(d!.driver_id, action),
    onSuccess: () => { fb.ok(); refreshDetail(); },
    onError: fb.fail,
  });

  return (
    <div className="mpage on">
      <PageHead eyebrow="Drivers · Edit Driver" title="Edit driver"
        desc="Every change to a driver happens here: name, KYC, phone number, lifecycle." />

      {!sel && (
        <>
          <div className="section-label">Pick a driver</div>
          <div className="panel panel-pad">
            <SearchInput placeholder="Search name / number / DRV id…" value={pick}
              onChange={(e) => setPick(e.target.value)} style={{ width: 300 }} autoFocus />
            {!hasTerm && (
              <p style={{ fontSize: 12, color: "var(--t-dim)", marginTop: 10 }}>
                Type a name, number, or DRV id to find the driver.
              </p>
            )}
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              {hasTerm && (results.data?.drivers ?? []).map((r) => (
                <button key={r.driver_id} className="btn ghost"
                  style={{ justifyContent: "flex-start", display: "flex", gap: 12 }}
                  onClick={() => setParams({ id: r.driver_id })}>
                  <span className="mono">{r.code}</span>
                  <span style={{ fontWeight: 600 }}>{r.name}</span>
                  <span className="mono muted">{r.msisdn}</span>
                  <DriverChip st={r.status} />
                </button>
              ))}
              {hasTerm && results.isSuccess && (results.data?.drivers.length ?? 0) === 0 && (
                <span className="cap">No match.</span>
              )}
            </div>
          </div>
        </>
      )}

      {sel && d && (
        <>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
            <span className="mono muted">{d.code}</span>
            <span style={{ fontWeight: 700, fontSize: 16 }}>{d.name}</span>
            <span className="mono muted">{d.msisdn}</span>
            <DriverChip st={d.status} />
            <button className="btn ghost" style={{ marginLeft: "auto" }}
              onClick={() => { setParams({}); setPick(""); }}>
              <Icon name="swap" /> Switch driver
            </button>
          </div>

          <div className="section-label">Photo</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <PhotoSection d={d} onSaved={refreshDetail} />
          </div>

          <div className="section-label">Name</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <SearchInput value={newName}
                onChange={(e) => setNewName(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
              <button className="btn ghost" disabled={!nameDirty || renameMut.isPending}
                onClick={() => renameMut.mutate()}>
                Save name
              </button>
            </div>
          </div>

          <div className="section-label">Phone number</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <ChangeNumber d={d} onDone={refreshDetail} />
          </div>

          <div className="section-label">KYC</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <KycSection d={d} onSaved={refreshDetail} />
          </div>

          <div className="section-label">Lifecycle</div>
          <div className="panel panel-pad">
            <KV k="Status" v={d.status} />
            {d.blocked_at && <KV k="Blocked" v={dateTime(d.blocked_at)} accent="var(--bad)" />}
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {d.status !== "blocked" && d.status !== "removed" && (
                <button className="btn ghost" onClick={() =>
                  ask(<>Block <b>{d.name}</b>? Their app sessions are revoked and the app stops working.</>,
                    () => lifecycle.mutate("block"), "Block")}>
                  Block
                </button>
              )}
              {d.status === "blocked" && (
                <button className="btn ghost" onClick={() => lifecycle.mutate("unblock")}>
                  Unblock
                </button>
              )}
              {d.status !== "removed" && (
                <button className="btn red" onClick={() =>
                  ask(<>Remove <b>{d.name}</b>? This is an offboarding — history is kept, but the identity is closed.</>,
                    () => lifecycle.mutate("remove"), "Remove")}>
                  Remove
                </button>
              )}
              {fb.errorNode}
            </div>
          </div>

          {/* everything the platform knows, read-only — same panel as the
              Overview drawer; edits above never touch these facts */}
          <div className="section-label tight">Info — read-only</div>
          <div className="panel panel-pad">
            <DriverInfoPanel d={d} />
          </div>
        </>
      )}
      {dialog}
    </div>
  );
}
